/**
 * Video Overlay Library Utilities
 *
 * Utilities for managing recently used video tracks in localStorage.
 * Stores full VideoOverlayTrack objects for display without additional API calls.
 */

import { VideoOverlayTrack } from './types';
import { ASSET_PATH } from '../constants';
import appLogger from '@/services/logger';

const STORAGE_KEY = 'content-studio:recently-used-video-overlays';
const MAX_RECENT_TRACKS = 3;

/**
 * Get the list of recently used video tracks.
 * Returns an array of VideoOverlayTrack objects in order of most recent first.
 */
export const getRecentlyUsed = (): VideoOverlayTrack[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (item): item is VideoOverlayTrack =>
        item &&
        typeof item.id === 'string' &&
        typeof item.name === 'string' &&
        typeof item.videoUrl === 'string' &&
        typeof item.thumbnailUrl === 'string' &&
        typeof item.duration === 'number' &&
        Array.isArray(item.tags) &&
        item.tags.every(tag => typeof tag === 'string')
    );
  } catch (error) {
    appLogger.error('Error reading recently used videos from localStorage', error);
    return [];
  }
};

/**
 * Add a video track to the recently used list.
 * The track will be added to the front of the list.
 * If the track already exists (by id), it will be moved to the front.
 * Enforces a maximum of MAX_RECENT_TRACKS items.
 *
 * @param track - The VideoOverlayTrack to add
 */
export const addToRecentlyUsed = (track: VideoOverlayTrack): void => {
  try {
    if (!track || !track.id) {
      return;
    }

    const current = getRecentlyUsed();
    const filtered = current.filter(t => t.id !== track.id);
    const updated = [track, ...filtered];
    const limited = updated.slice(0, MAX_RECENT_TRACKS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch (error) {
    appLogger.error('Error saving recently used videos to localStorage', error);
  }
};

/**
 * Create a VideoOverlayTrack from an uploaded file with a backend URL.
 *
 * @param file - The uploaded file
 * @param url - The backend URL for the uploaded video
 * @returns A VideoOverlayTrack object representing the custom upload
 */
export const createCustomTrackFromUrl = (file: File, url: string): VideoOverlayTrack => {
  return {
    id: url,
    name: file.name,
    thumbnailUrl: '',
    videoUrl: url,
    duration: 0,
    tags: []
  };
};

/**
 * Check if a video track is a custom upload (not from library).
 * A custom upload is determined if its videoUrl contains the ASSET_PATH.
 * Handles URL-encoded paths by decoding the URL before checking.
 *
 * @param track - The VideoOverlayTrack to check
 * @returns true if the track is a custom upload, false if it's from the library
 */
export const isCustomUpload = (track: VideoOverlayTrack): boolean => {
  if (!track?.id || !track.videoUrl) {
    return false;
  }
  try {
    return decodeURIComponent(track.videoUrl).includes(ASSET_PATH);
  } catch {
    return track.videoUrl.includes(ASSET_PATH);
  }
};

/**
 * Clear all recently used tracks from localStorage.
 * Useful for testing or user preference to clear history.
 */
export const clearRecentlyUsed = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    appLogger.error('Error clearing recently used videos from localStorage', error);
  }
};
